import { useRef } from "react";
import { useLang } from "@/lib/i18n";
import { FUELS, GEARS, BODIES } from "@/lib/format";
import { Field } from "./ui";
import { BrandPicker } from "./BrandPicker";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const EMPTY_SPECS = { brand: "", model: "", trim: "", year: "", mileage: "", hand: "", fuel: "petrol", gearbox: "automatic", body_type: "sedan", color: "", interior_color: "", engine: "", horsepower: "", title_he: "", title_ar: "", desc_he: "", desc_ar: "" };

export const autoTitle = (s) => [s.brand, s.model, s.trim, s.year].map((x) => String(x ?? "").trim()).filter(Boolean).join(" ");
const AUTO_KEYS = ["brand", "model", "trim", "year"];

export const specsPayload = (s) => ({
  ...s,
  year: Number(s.year), mileage: Number(s.mileage), hand: Number(s.hand),
  horsepower: s.horsepower === "" || s.horsepower == null ? null : Number(s.horsepower),
});

export const pickSpecs = (v) => Object.fromEntries(Object.keys(EMPTY_SPECS).map((k) => [k, v?.[k] ?? ""]));

const Opt = ({ k, list, prefix, value, set }) => {
  const { t } = useLang();
  return (
    <Field label={t(`spec.${k}`)}>
      <Select value={value[k]} onValueChange={(x) => set(k, x)}>
        <SelectTrigger className="xd-input" data-testid={`spec-${k}-select`}><SelectValue /></SelectTrigger>
        <SelectContent>{list.map((o) => <SelectItem key={o} value={o}>{t(`${prefix}.${o}`)}</SelectItem>)}</SelectContent>
      </Select>
    </Field>
  );
};

export const SpecsForm = ({ value, onChange, textActions }) => {
  const { t } = useLang();
  const latest = useRef(value);
  latest.current = value;
  const set = (k, v) => {
    const value = latest.current;
    const next = { ...value, [k]: v };
    if (AUTO_KEYS.includes(k)) {
      const before = autoTitle(value);
      ["he", "ar"].forEach((l) => { if (!value[`title_${l}`] || value[`title_${l}`] === before) next[`title_${l}`] = autoTitle(next); });
    }
    onChange(next);
    latest.current = next;
  };
  const LIMITS = { year: { min: 1950, max: new Date().getFullYear() + 1 }, mileage: { min: 0 }, hand: { min: 0, max: 20 }, horsepower: { min: 0 } };
  const inp = (k, type = "text", req = true, hint) => (
    <Field label={`${t(`spec.${k}`)}${req ? "" : ` · ${t("form.optional")}`}`} hint={hint}>
      <Input required={req} type={type} {...(LIMITS[k] || {})} dir={type === "number" ? "ltr" : undefined} className="xd-input" value={value[k]} onChange={(e) => set(k, e.target.value)} data-testid={`spec-${k}-input`} />
    </Field>
  );
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Field label={t("spec.brand")} hint={t("spec.brand_hint")}>
          <BrandPicker value={value.brand} onChange={(b) => b !== latest.current.brand && set("brand", b)} />
        </Field>
        {inp("model")}{inp("trim", "text", false, t("spec.trim_hint"))}{inp("year", "number")}{inp("mileage", "number")}{inp("hand", "number", true, t("spec.hand_hint"))}
        {inp("color")}{inp("interior_color", "text", false)}
        <Opt k="fuel" list={FUELS} prefix="fuel" value={value} set={set} />
        <Opt k="gearbox" list={GEARS} prefix="gear" value={value} set={set} />
        <Opt k="body_type" list={BODIES} prefix="body" value={value} set={set} />
        {inp("engine", "text", false)}{inp("horsepower", "number", false)}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-zinc-400">{t("spec.texts_hint")} {t("spec.auto_title_hint")}</p>
        {textActions}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {["he", "ar"].map((l) => (
          <div key={l} className="space-y-4">
            <Field label={`${t("spec.title")} · ${l === "he" ? "עברית" : "العربية"}`}>
              <Input className="xd-input" value={value[`title_${l}`]} onChange={(e) => set(`title_${l}`, e.target.value)} data-testid={`spec-title-${l}-input`} />
            </Field>
            <Field label={`${t("spec.description")} · ${l === "he" ? "עברית" : "العربية"}`}>
              <Textarea rows={6} className="xd-input" value={value[`desc_${l}`]} onChange={(e) => set(`desc_${l}`, e.target.value)} data-testid={`spec-desc-${l}-input`} />
            </Field>
          </div>
        ))}
      </div>
    </div>
  );
};

export const PhotoUploader = ({ onFiles, busy, testid = "photo-upload-input" }) => {
  const { t } = useLang();
  return (
    <label className={`btn-ghost cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`}>
      <input type="file" multiple accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif" className="hidden" data-testid={testid}
        onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ""; if (f.length) onFiles(f); }} />
      {busy ? t("photos.uploading") : t("photos.upload")}
    </label>
  );
};
